import type {
  CreateTaskTypeDef,
  StatusConfig,
  StatusCustomMapping,
  TaskCapabilities,
  TaskTypeBundle,
  TaskTypeDef,
  UpdateTaskTypeDef,
} from '@scrambl/shared'

import { apiClient } from './api-client'

export type TaskTypeWithCount = TaskTypeDef & { taskCount: number }

/**
 * A project's enabled type, annotated with where its statuses come from
 * within that project (only returned by listForProject).
 */
export type ProjectTaskType = TaskTypeWithCount & {
  statusSource: 'ORG_DEFAULT' | 'PROJECT_DEFAULT' | 'CUSTOM'
}

export interface TaskTypeTemplateInfo {
  key: string
  name: string
  pluralName: string
  description: string
  icon: string
  color: string
  bundle: TaskTypeBundle
  capabilities: TaskCapabilities
  statusCount: number
  statuses: StatusConfig[]
  defaultPriority: string | null
  installed: boolean
}

export interface TaskTypeProjectRef {
  id: string
  name: string
  /** Included via the all-enabled fallback rather than an explicit subset choice. */
  viaDefault: boolean
  /** Number of tasks of this type within the project. */
  taskCount: number
}

export const taskTypesApi = {
  list: async (includeArchived = false): Promise<TaskTypeWithCount[]> =>
    apiClient<TaskTypeWithCount[]>(
      `/task-types${includeArchived ? '?includeArchived=true' : ''}`,
    ),

  listProjectsUsingType: async (id: string): Promise<TaskTypeProjectRef[]> =>
    apiClient<TaskTypeProjectRef[]>(`/task-types/${id}/projects`),

  get: async (id: string): Promise<TaskTypeWithCount> =>
    apiClient<TaskTypeWithCount>(`/task-types/${id}`),

  listTemplates: async (): Promise<TaskTypeTemplateInfo[]> =>
    apiClient<TaskTypeTemplateInfo[]>('/task-types/templates'),

  create: async (dto: CreateTaskTypeDef): Promise<TaskTypeDef> =>
    apiClient<TaskTypeDef>('/task-types', { method: 'POST', body: dto }),

  install: async (templateKey: string): Promise<TaskTypeWithCount> =>
    apiClient<TaskTypeWithCount>('/task-types/install', {
      method: 'POST',
      body: { templateKey },
    }),

  update: async (id: string, dto: UpdateTaskTypeDef): Promise<TaskTypeDef> =>
    apiClient<TaskTypeDef>(`/task-types/${id}`, {
      method: 'PATCH',
      body: dto,
    }),

  setEnabled: async (id: string, isEnabled: boolean): Promise<TaskTypeDef> =>
    apiClient<TaskTypeDef>(`/task-types/${id}/enabled`, {
      method: 'PATCH',
      body: { isEnabled },
    }),

  setArchived: async (id: string, isArchived: boolean): Promise<TaskTypeDef> =>
    apiClient<TaskTypeDef>(`/task-types/${id}/archive`, {
      method: 'PATCH',
      body: { isArchived },
    }),

  reorder: async (ids: string[]): Promise<{ count: number }> =>
    apiClient<{ count: number }>('/task-types/reorder', {
      method: 'PATCH',
      body: { ids },
    }),

  remove: async (
    id: string,
    reassignToId?: string,
  ): Promise<{ success: boolean; reassignedCount: number }> =>
    apiClient<{ success: boolean; reassignedCount: number }>(
      `/task-types/${id}${
        reassignToId ? `?reassignTo=${encodeURIComponent(reassignToId)}` : ''
      }`,
      { method: 'DELETE' },
    ),

  getStatuses: async (id: string): Promise<StatusConfig[]> =>
    apiClient<StatusConfig[]>(`/task-types/${id}/statuses`),

  setStatuses: async (
    id: string,
    statuses: StatusConfig[],
    options?: {
      // Remap tasks in projects inheriting these org defaults (default true).
      migrateExistingTasks?: boolean
      customMappings?: StatusCustomMapping[]
    },
  ): Promise<StatusConfig[]> =>
    apiClient<StatusConfig[]>(`/task-types/${id}/statuses`, {
      method: 'PUT',
      body: { statuses, ...options },
    }),

  /* ----- per-project subset ----- */

  listForProject: async (projectId: string): Promise<ProjectTaskType[]> =>
    apiClient<ProjectTaskType[]>(`/task-types/projects/${projectId}`),

  addToProject: async (
    projectId: string,
    taskTypeDefId: string,
  ): Promise<TaskTypeWithCount[]> =>
    apiClient<TaskTypeWithCount[]>(`/task-types/projects/${projectId}`, {
      method: 'POST',
      body: { taskTypeDefId },
    }),

  removeFromProject: async (
    projectId: string,
    taskTypeDefId: string,
  ): Promise<TaskTypeWithCount[]> =>
    apiClient<TaskTypeWithCount[]>(
      `/task-types/projects/${projectId}/${taskTypeDefId}`,
      { method: 'DELETE' },
    ),

  addToAllProjects: async (id: string): Promise<{ count: number }> =>
    apiClient<{ count: number }>(`/task-types/${id}/add-to-all-projects`, {
      method: 'POST',
    }),
}
